import { useEffect, useState, useCallback, useMemo } from 'react';
import {
  useGetTotalFinalPriceChangeOrderCloseouts,
  useGetTotalFinalPriceCloseouts,
  useUpdateTotalFinalPriceChangeOrderCloseoutMutation,
  useUpdateTotalFinalPriceCloseoutMutation,
} from '../../hooks/useCloseoutHooks';
import { TotalPricingRow } from './TotalPricingRow';
import { PricingRowListName } from './closeoutTypes';
import { TotalFinalPriceCloseout } from '../../api/protosCompiled/estimateCloseout/estimateCloseout_pb';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  alpha,
  Card,
  CardContent,
  useMediaQuery,
} from '@mui/material';
import { formatCurrency, formatNumber } from './closeoutHelpers';
import { useStore } from 'zustand';
import { useUnityBuildStore } from '../../stores/store';
import { TotalFinalPriceChangeOrderCloseout } from '../../api/protosCompiled/changeOrderCloseout/changeOrderCloseout_pb';
import { SellingPriceInput } from './SellingPriceInput';
import { useLockBid } from '../../utils/useLockBid';

type TotalFinalCloseout =
  | TotalFinalPriceCloseout.AsObject
  | TotalFinalPriceChangeOrderCloseout.AsObject;

export const TotalPricingHeader = () => {
  const theme = useTheme();
  const { lockBid } = useLockBid();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const selectedEstimate = useStore(useUnityBuildStore).selectedEstimate;

  const { data: estimateTotalFinalPrice, refetch: refetchEstimateCloseout } =
    useGetTotalFinalPriceCloseouts();
  const { mutate: updateEstimateCloseout } = useUpdateTotalFinalPriceCloseoutMutation();

  const { data: changeOrderTotalFinalPrice, refetch: refetchChangeOrder } =
    useGetTotalFinalPriceChangeOrderCloseouts();
  const { mutate: updateChangeOrderCloseout } =
    useUpdateTotalFinalPriceChangeOrderCloseoutMutation();

  const refetch = selectedEstimate?.estimateid ? refetchEstimateCloseout : refetchChangeOrder;

  const updateCloseout = selectedEstimate?.estimateid
    ? updateEstimateCloseout
    : updateChangeOrderCloseout;

  const totalFinalPrice = selectedEstimate?.estimateid
    ? estimateTotalFinalPrice
    : changeOrderTotalFinalPrice;

  // Ensure totalFinalPriceData is defined
  const totalFinalPriceData = useMemo<TotalFinalCloseout>(() => {
    return totalFinalPrice?.closeouts ?? ({} as TotalFinalCloseout);
  }, [totalFinalPrice]);

  // Function to extract the correct closeout object from the row data.
  const getCloseoutObject = (rowData: TotalFinalCloseout, closeoutName: PricingRowListName) => {
    switch (closeoutName) {
      case 'extendestimate':
        return rowData.extendestimate;
      case 'directlaborcloseout':
        return rowData.directlaborcloseout;
      case 'laborfactoringcloseout':
        return rowData.laborfactoringcloseout;
      case 'incidentallaborcloseout':
        return rowData.incidentallaborcloseout;
      case 'indirectlaborcloseout':
        return rowData.indirectlaborcloseout;
      case 'equipmentcloseout':
        return rowData.equipmentcloseout;
      case 'generalexpensecloseout':
        return rowData.generalexpensecloseout;
      case 'subcontractcloseout':
        return rowData.subcontractcloseout;
      case 'quotecloseout':
        return rowData.quotecloseout;
      default:
        return null;
    }
  };

  const [sellingPrice, setSellingPrice] = useState<string>(
    formatNumber(totalFinalPriceData?.sellingprice || 0)
  );
  const [lastSellingPrice, setLastSellingPrice] = useState<string>(
    formatNumber(totalFinalPriceData?.sellingprice || 0)
  );

  useEffect(() => {
    if (totalFinalPrice) {
      setSellingPrice(formatNumber(totalFinalPriceData?.sellingprice || 0));
      setLastSellingPrice(formatNumber(totalFinalPriceData?.sellingprice || 0));
    }
  }, [totalFinalPriceData, totalFinalPrice]);

  // Define Field as the allowed key from the TotalFinalPriceCloseout.AsObject.
  type Field = keyof Pick<TotalFinalPriceCloseout.AsObject, 'sellingprice'>;
  // This is equivalent to: "sellingprice"

  // Change handler: update local state.
  const handleChange = useCallback(
    (field: Field, setter: React.Dispatch<React.SetStateAction<string>>) =>
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        // Allow only numbers with up to 2 decimals (or empty string)
        if (/^\d*\.?\d{0,2}$/.test(value) || value === '') {
          setter(value);
        }
      },
    []
  );

  // Helper: round a string number to 2 decimals.
  const roundToTwoDecimals = (value: string): string => {
    const num = parseFloat(value);
    return isNaN(num) ? value : num.toFixed(2);
  };

  // Blur handler: if changed, update the backend.
  const handleBlur = useCallback(
    async (field: Field, value: string) => {
      const fixed = roundToTwoDecimals(value);
      setSellingPrice(fixed);
      if (fixed === lastSellingPrice) {
        return; // No change, avoid unnecessary update.
      }
      setLastSellingPrice(fixed);

      // Construct an updated closeout object.
      // Note: This object must conform to TotalFinalPriceCloseout.AsObject.
      const updatedCloseout: TotalFinalPriceCloseout.AsObject = {
        ...totalFinalPriceData,
        sellingprice: parseFloat(fixed),
      };
      updateCloseout(updatedCloseout, {
        onSuccess: () => {
          refetch();
        },
      });
    },
    [lastSellingPrice, totalFinalPriceData, updateCloseout, refetch]
  );

  // Structured closeout data for rendering
  const closeoutData = [
    'extendestimate',
    'directlaborcloseout',
    'laborfactoringcloseout',
    'incidentallaborcloseout',
    'indirectlaborcloseout',
    'equipmentcloseout',
    'generalexpensecloseout',
    'subcontractcloseout',
    'quotecloseout',
  ] as PricingRowListName[];

  // Common styles
  const headerCellStyle = {
    fontWeight: 600,
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : '#f5f5f5',
    color: theme.palette.text.primary,
    fontSize: '0.875rem',
    padding: theme.spacing(1.5, 2),
    whiteSpace: 'nowrap' as const,
    borderBottom: `1px solid ${theme.palette.divider}`,
  };

  const footerRowStyle = {
    transition: 'background-color 0.2s',
    '&:hover': {
      backgroundColor:
        theme.palette.mode === 'dark'
          ? alpha(theme.palette.common.white, 0.1)
          : alpha(theme.palette.common.black, 0.04),
    },
    '&:nth-of-type(even)': {
      backgroundColor:
        theme.palette.mode === 'dark'
          ? alpha(theme.palette.common.white, 0.02)
          : alpha(theme.palette.common.black, 0.02),
    },
  };

  // Container styles
  const containerStyle = {
    width: '100%',
    height: 'calc(100vh - 200px)', // Adjust based on your layout needs
    minHeight: '600px',
    display: 'flex',
    flexDirection: isMobile ? ('column' as const) : ('row' as const),
    gap: 2,
    overflow: 'hidden',
    padding: theme.spacing(2),
  };

  // Table container styles
  const tableContainerStyle = {
    height: isMobile ? 'calc(100vh - 400px)' : '100%',
    width: isMobile ? '100%' : '70%',
    overflowY: 'auto',
    overflowX: 'auto',
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[1],
    borderRadius: theme.shape.borderRadius,
    flexShrink: 0,
  };

  // Summary card styles
  const summaryCardStyle = {
    width: isMobile ? '100%' : '30%',
    height: isMobile ? 'auto' : '100%',
    backgroundColor:
      theme.palette.mode === 'dark'
        ? theme.palette.background.paper
        : theme.palette.background.paper,
    boxShadow: theme.shadows[3],
    borderRadius: theme.shape.borderRadius,
    display: 'flex',
    flexDirection: 'column' as const,
    flexShrink: 0,
    overflow: 'auto',
  };

  const summaryCardContentStyle = {
    padding: 0,
    '&:last-child': { paddingBottom: 0 },
    height: '100%',
    display: 'flex',
    flexDirection: 'column' as const,
  };

  const summaryCellStyle = {
    fontWeight: 600,
    color: theme.palette.text.primary,
    padding: theme.spacing(1.5, 2),
    border: 'none',
    width: '70%',
  };

  const summaryValueCellStyle = {
    fontWeight: 600,
    color: theme.palette.text.primary,
    padding: theme.spacing(1.5, 2),
    border: 'none',
    textAlign: 'right' as const,
  };

  const sellingPriceRowStyle = {
    transition: 'background-color 0.2s',
    backgroundColor:
      theme.palette.mode === 'dark'
        ? alpha(theme.palette.primary.main, 0.15)
        : alpha(theme.palette.primary.main, 0.05),
    borderTop: `1px solid ${theme.palette.divider}`,
    marginTop: 'auto',
    '&:hover': {
      backgroundColor:
        theme.palette.mode === 'dark'
          ? alpha(theme.palette.primary.main, 0.25)
          : alpha(theme.palette.primary.main, 0.08),
    },
  };

  return (
    <Box sx={containerStyle} role="region" aria-label="Total Pricing Table">
      <TableContainer component={Paper} sx={tableContainerStyle}>
        <Table stickyHeader sx={{ tableLayout: 'auto', minWidth: 900 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={headerCellStyle}>Cost Description</TableCell>
              <TableCell sx={headerCellStyle}>$ Cost</TableCell>
              <TableCell sx={headerCellStyle}>% Tax / Adjust</TableCell>
              <TableCell sx={headerCellStyle}>$ Tax / Adjust</TableCell>
              <TableCell sx={headerCellStyle}>% Overhead</TableCell>
              <TableCell sx={headerCellStyle}>$ Overhead</TableCell>
              <TableCell sx={headerCellStyle}>% Profit</TableCell>
              <TableCell sx={headerCellStyle}>$ Profit</TableCell>
              <TableCell sx={headerCellStyle}>Total Price</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {closeoutData.map(closeoutName => {
              const closeoutObject = getCloseoutObject(totalFinalPriceData, closeoutName);
              if (!closeoutObject) return null;

              return (
                <TotalPricingRow
                  key={closeoutName}
                  closeoutName={closeoutName}
                  closeout={closeoutObject}
                  rowData={totalFinalPriceData}
                />
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Card sx={summaryCardStyle}>
        <CardContent sx={summaryCardContentStyle}>
          <Table size="small" sx={{ height: '100%' }}>
            <TableBody>
              <TableRow sx={footerRowStyle}>
                <TableCell sx={summaryCellStyle}>Total Cost + Tax/Adjust</TableCell>
                <TableCell sx={summaryValueCellStyle}>
                  {formatCurrency(totalFinalPriceData.totalcost)}
                </TableCell>
              </TableRow>

              <TableRow sx={footerRowStyle}>
                <TableCell sx={summaryCellStyle}>Total Overhead</TableCell>
                <TableCell sx={summaryValueCellStyle}>
                  {formatCurrency(totalFinalPriceData.totaloverhead)}
                </TableCell>
              </TableRow>

              <TableRow sx={footerRowStyle}>
                <TableCell sx={summaryCellStyle}>Total Profit</TableCell>
                <TableCell sx={summaryValueCellStyle}>
                  {formatCurrency(totalFinalPriceData.totalprofit)}
                </TableCell>
              </TableRow>

              <TableRow sx={footerRowStyle}>
                <TableCell sx={summaryCellStyle}>Total Estimate</TableCell>
                <TableCell sx={summaryValueCellStyle}>
                  {formatCurrency(totalFinalPriceData.totalfinalprice)}
                </TableCell>
              </TableRow>

              <TableRow sx={sellingPriceRowStyle}>
                <TableCell
                  sx={{
                    ...summaryCellStyle,
                    fontWeight: 700,
                    padding: theme.spacing(2),
                  }}
                >
                  Total Selling Price
                </TableCell>
                <TableCell
                  sx={{
                    padding: theme.spacing(2),
                    border: 'none',
                    width: '100%',
                    minWidth: '200px',
                    maxWidth: '300px',
                  }}
                >
                  <SellingPriceInput
                    sellingPrice={sellingPrice}
                    setSellingPrice={setSellingPrice}
                    lastSellingPrice={lastSellingPrice}
                    setLastSellingPrice={setLastSellingPrice}
                    handleBlur={handleBlur}
                    handleChange={handleChange}
                    width="100%"
                    disabled={lockBid()}
                  />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </Box>
  );
};

export default TotalPricingHeader;
